/**
 * UI-only slice for the unified property flow. The PropertyView itself is
 * derived from RTK Query caches — this slice only tracks filter/sort and
 * per-property session UI flags.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type PropertyFilter = 'all' | 'purchase' | 'refinance' | 'dscr';
export type PropertySort = 'updated' | 'stage' | 'price';

export interface PropertyUiState {
  filter: PropertyFilter;
  sort: PropertySort;
  /** Session-only dismissals of the amber idle border per property. */
  idleDismissed: Record<string, boolean>;
  /** Stage panels the user has explicitly opened for review (locked stages). */
  reviewOverrides: Record<string, number>;
}

const initialState: PropertyUiState = {
  filter: 'all',
  sort: 'updated',
  idleDismissed: {},
  reviewOverrides: {},
};

const propertyUiSlice = createSlice({
  name: 'propertyUi',
  initialState,
  reducers: {
    setFilter(state, action: PayloadAction<PropertyFilter>) {
      state.filter = action.payload;
    },
    setSort(state, action: PayloadAction<PropertySort>) {
      state.sort = action.payload;
    },
    dismissIdle(state, action: PayloadAction<string>) {
      state.idleDismissed[action.payload] = true;
    },
    clearIdleDismissals(state) {
      state.idleDismissed = {};
    },
    setReviewOverride(
      state,
      action: PayloadAction<{ propertyId: string; stage: number }>
    ) {
      state.reviewOverrides[action.payload.propertyId] = action.payload.stage;
    },
    clearReviewOverride(state, action: PayloadAction<string>) {
      delete state.reviewOverrides[action.payload];
    },
  },
});

export const {
  setFilter,
  setSort,
  dismissIdle,
  clearIdleDismissals,
  setReviewOverride,
  clearReviewOverride,
} = propertyUiSlice.actions;

export default propertyUiSlice.reducer;
